<template>
  <div ref="scrollContainer" class="patient-list" @scroll.passive="onScroll">
    <!-- Robot Entry -->
    <RobotEntry @toggle="$emit('toggleNotifications')" />

    <!-- Search Bar -->
    <div class="search-bar-wrapper">
      <SingleSelector
        v-model="selectedPatientValue"
        size="medium"
        placeholder="Search patient"
        :options="patientOptions"
        :loading="dropdownSearching"
        loading_text="Searching…"
        no_data_text="No patients found"
        filterable
        remote_search
        clearable
        customizeOptionItem
        @remote-search="onRemoteSearch"
        @change="onPatientChange"
      >
        <template #suffix>
          <div class="search-suffix-icon">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
        </template>
        <template #option_item="{ option, select }">
          <div
            :key="option.value"
            class="search-dropdown-item px-4 py-3 flex items-center gap-3 cursor-pointer"
            @click="select()"
          >
            <div class="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center">👤</div>
            <div class="min-w-0">
              <div class="font-semibold truncate">{{ option.label }}</div>
              <div class="text-sm text-gray-600 truncate">
                {{ formatDateToMMDDYYYY(option.patient.dob) }}<span v-if="option.patient.phone"> · {{ option.patient.phone }}</span>
              </div>
            </div>
          </div>
        </template>
      </SingleSelector>
    </div>

    <!-- Error -->
    <div v-if="patientLoadError" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm text-red-800">{{ patientLoadError }}</p>
        </div>
        <div class="ml-auto pl-3">
          <Button
            type="narrow"
            color-type="dark-blue-stroke"
            @click="$emit('retryLoadPatients')"
          >
            Retry
          </Button>
        </div>
      </div>
    </div>

    <!-- Add New Patient -->
    <ResponsiveButton
      class="mb-8"
      button_type="filled"
      built_in_theme="dark"
      width_type="fill-whole"
      size="medium"
      display_name="Add New Patient"
      @click-button="openAddPatientModal"
    >
      <template #prefix>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M16 9H9V16H7V9H0V7H7V0H9V7H16V9Z" fill="white"/>
        </svg>
      </template>
    </ResponsiveButton>

    <!-- List -->
    <Loading 
      v-if="isLoadingRoomList"  
      :loading="isLoadingRoomList" 
      loadingText="Loading rooms..." 
    />
    <div v-else class="flex flex-col gap-1">
      <!-- Support Room (always at top) -->
      <div 
        v-if="supportRoom"
        :key="supportRoom.room_id"
        class="support-room-item relative flex items-start py-3 px-4 gap-2 cursor-pointer"
        :class="{ 'active-room': currentRoomId === supportRoom.room_id }"
        @click="selectSupportRoom"
      >
        <div class="shrink-0">
          <div class="support-room-icon flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </div>
        </div>

        <div class="flex-1">
          <div class="flex items-start gap-2 justify-between">
            <div>
              <div class="support-room-name">Feedback</div>
              <div class="support-room-subtitle">Talk to our team</div>
            </div>
            <div class="flex items-center gap-2">
              <div
                v-if="supportRoomUnreadCount > 0"
                class="unread-badge-inline"
              >
                {{ supportRoomUnreadCount > 99 ? '99+' : supportRoomUnreadCount }}
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Patient Rooms -->
      <div 
        v-for="room in roomList" 
        :key="room.roomId || room.patientBasicInfo?.patientId"
        :data-patient-id="room.patientBasicInfo?.patientId"
        :class="['patient-item relative flex items-start py-3 px-4 gap-2 cursor-pointer', { 'selected': room.roomId && room.roomId === currentRoomId }]"
        @click="handleRoomClick(room)" 
      >
        <div class="shrink-0">
          <img v-if="room.avatar" :src="room.avatar" alt="avatar" class="w-10 h-10 rounded-full" />
          <div v-else class="name-initial flex items-center justify-center">
            {{ getInitials(room.roomName) }}
          </div>
        </div>

        <div>
          <div class="flex items-start gap-2 justify-between">
            <div>
              <div class="patient-name">{{ room.roomName }}</div>
              <div class="last-message-time">{{ getTimeAgo(room.lastMessageTime) }}</div>
            </div>
            <div
              v-if="room.unreadCount > 0"
              class="unread-badge-inline"
            >
              {{ room.unreadCount > 99 ? '99+' : room.unreadCount }}
            </div>
          </div>
          <!-- Only show when there IS a last message -->
          <div v-if="room.lastMessage" class="last-message-preview line-clamp-2">
            {{ room.lastMessage }}
          </div>
        </div>
      </div>

      <!-- No results -->
      <div v-if="roomList.length === 0 && inputPatientName" class="py-8 text-center text-gray-500">
        <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
        </svg>
        <p class="text-sm">No patients found</p>
        <p class="text-xs text-gray-400 mt-1">Try adjusting your search terms</p>
      </div>

      <!-- Empty -->
      <div v-if="roomList.length === 0 && !inputPatientName && !isLoadingRoomList" class="py-8 text-center text-gray-500">
        <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
        </svg>
        <p class="text-sm">No patients yet</p>
      </div>
    </div>

    <!-- Loading more patients spinner -->
    <div v-if="isLoadingMore" class="loading-more-container">
      <ArrowSmall class="rotating-circle" />
      <span class="loading-more-text">Loading more...</span>
    </div>

    <!-- Modal -->
    <AddPatientModal
      v-model="showAddPatientModal"
      @save="handleSavePatient"
    />
    
  </div>
</template>

<script>
import AddPatientModal from './AddPatientModal.vue'
import RobotEntry from './notification-tabs/RobotEntry.vue';
import Loading from '@/NewLibrary/Loading/Loading.vue';
import ArrowSmall from '@/NewLibrary/Loading/ArrowSmall.vue';
import Button from '@/NewLibrary/Button.vue';
import { searchPatientApi } from '@/api/orderTest'
import { getLastSeenApi, markAsReadApi, getRoomsApi, createRoomApi } from '@/api/ehrRooms';
import { getPatientListApi } from '@/api/patientList';
import { LIST_PATIENTS } from '@/graphql/queries';
import { mapState } from 'vuex';
import moment from 'moment';
import _ from 'lodash';
import { getAvatar } from '@/utils/dummy-avatar';
import { getLastMessagePreview } from './room-list';

export default {
  components: { AddPatientModal, RobotEntry, Loading, ArrowSmall, Button },
  props: {
    patientLoadError: { type: String, default: null },
  },
  emits: ["clickRoom", "retryLoadPatients", "creatingRoom", "roomListLoaded", "openNotifications", "toggleNotifications"],
  data() {
    return {
      isLoadingRoomList: false,
      inputPatientName: '',
      selectedPatientValue: '',
      showAddPatientModal: false,
      searchPatientDropdown: [],
      isSearchFocused: false,
      dropdownSearching: false,
      findPatientsPage: 0,
      findPatientsPerPage: 20,
      findPatientsTotal: 0,
      isLoadingMore: false,
      roomPatientIds: new Set(),
    }
  },
  computed: {
    ...mapState('login', ['userInfo']),
    ...mapState('myPatients/rooms', ['currentRoomId', 'roomList']),
    supportRoom() {
      return this.$store.state.login?.support_room || null;
    },
    supportRoomUnreadCount() {
      if (!this.supportRoom?.room_id) return 0;
      const lastSeenData = this.$store.getters['myPatients/rooms/getLastSeenByRoomId'](this.supportRoom.room_id);
      return lastSeenData?.unread || 0;
    },
    showDropdown() {
      const hasData = this.dropdownSearching || this.searchPatientDropdown.length > 0;
      return this.inputPatientName && this.isSearchFocused && hasData;
    },
    patientOptions() {
      return this.searchPatientDropdown.map((p) => ({
        value: p.patientId,
        label: this.getPatientName(p),
        patient: p,
      }));
    },
    clinicId() {
      return this.userInfo?.clinic_id || "";
    },
    hasMorePatients() {
      if (this.findPatientsPage === 0) return true;
      return this.findPatientsPage * this.findPatientsPerPage < this.findPatientsTotal;
    },
  },
  created() {
    this.debounceSearchPatient = _.debounce(this.searchPatient, 500);
  },
  async mounted() {
    this.$store.dispatch('myPatients/rooms/setCurrentRoomId', null);
    this.isLoadingRoomList = true;
    let roomList = await this.getRoomList();
    await this.addUnreadCountToRoomList();
    this.$store.dispatch('myPatients/rooms/setRoomList', roomList);
    
    // Fetch customers for the clinic after rooms are loaded
    if (this.clinicId) {
      this.$store.dispatch('myPatients/practiceInfo/fetchCustomersByClinicId', this.clinicId);
    }
    
    this.roomPatientIds = new Set(
      roomList.map(r => r.patientBasicInfo?.patientId?.toString()).filter(Boolean)
    );
    await this.loadRemainingPatients();
    this.isLoadingRoomList = false;
    
    // Emit event to notify parent that room list is fully loaded
    this.$emit('roomListLoaded');
  },

  methods: {
    async getRoomList() {
      try {
        let { data: roomsData } = await getRoomsApi({ limit: 200 });
        let patientIds = roomsData.map(r => r.ehr.patient_id);
        let listPatientsResult = await this.$apollo.provider.clients.myPatients.query({
          query: LIST_PATIENTS,
          variables: {
            patientIds: patientIds
          }
        });
        let roomList = [];
        let patientList = [];
        let listPatients = listPatientsResult?.data?.listpatients;
        if (listPatients && listPatients.errorMessage === 'Success') {
          patientList = listPatients?.data?.response || [];
        }
        roomsData.forEach(room => {
          let patientId = room.ehr.patient_id;
          let patient = { ...patientList.find(p => p.patient_id.toString() === patientId.toString()) };
          if (patient) {
            patient.patient_email = patient.patient_contact?.find(contact => contact.contact_description === "email")?.contact_details || '';
            patient.patient_phone = patient.patient_contact?.find(contact => contact.contact_description === "phone")?.contact_details || '';
            let patientAddress = patient.patient_address?.find(address => address.address_type === "shipping") || {};
            patient.patient_address = {
              patient_street_address: patientAddress.street_address || '',
              patient_apt_po: patientAddress.apt_po || '',
              patient_city: patientAddress.city || '',
              patient_state: patientAddress.state || '',
              patient_zip_code: patientAddress.zipcode || '',
              patient_country: patientAddress.country || '',
            }
          }
          if (!patient.patient_id) {
            patient.patient_id = patientId; // ensure patient_id is always present
          }
          let patientBasicInfo = this.mapBackendPatient(patient);
          let { firstName, middleName, lastName } = patientBasicInfo;
          
          // Filter out patients without any name data
          if (!firstName && !middleName && !lastName) {
            console.log('⚠️ Skipping patient without name data:', patientId);
            return; // Skip this patient
          }
          
          let result = {
            roomId: room.rid || null,
            roomName: this.getPatientName(patientBasicInfo),
            lastMessage: getLastMessagePreview(room?.lastMessage),
            lastMessageTime: room?.lastMessage?.ts || null,
            unreadCount: 0,
            patientBasicInfo,
            avatar: getAvatar(firstName, middleName, lastName),
          }
          roomList.push(result);
        });
        return roomList;
      } catch (err) {
        console.error('getRoomList error:', err);
        return [];
      }
    },

    async loadRemainingPatients() {
      if (this.isLoadingMore) return;
      const isInitialLoad = this.findPatientsPage === 0;
      try {
        if (!isInitialLoad) this.isLoadingMore = true;
        const nextPage = this.findPatientsPage + 1;
        const response = await getPatientListApi({
          page: nextPage.toString(),
          perPage: '20',
        });

        if (response && Array.isArray(response.patients)) {
          this.findPatientsTotal = response.total_count || 0;
          this.findPatientsPage = nextPage;

          const newPatients = response.patients
            .filter(p => !this.roomPatientIds.has(p.patient_id?.toString()))
            .map(patient => {
              let adapted = {
                ...patient,
                patient_phone: patient.patient_telephone || '',
                patient_address: {
                  patient_street_address: patient.patient_street_address || '',
                  patient_apt_po: '',
                  patient_city: patient.patient_city || '',
                  patient_state: patient.patient_state || '',
                  patient_zip_code: patient.patient_zip_code || '',
                  patient_country: patient.patient_country || '',
                }
              };
              let patientBasicInfo = this.mapBackendPatient(adapted);
              return {
                roomId: null,
                roomName: this.getPatientName(patientBasicInfo),
                lastMessage: null,
                lastMessageTime: null,
                unreadCount: 0,
                patientBasicInfo,
                avatar: getAvatar(patientBasicInfo.firstName, patientBasicInfo.middleName, patientBasicInfo.lastName),
              };
            })
            .filter(patient => {
              // Filter out patients without any name data
              const { firstName, middleName, lastName } = patient.patientBasicInfo;
              const hasName = firstName || middleName || lastName;
              if (!hasName) {
                console.log('⚠️ Skipping patient without name data:', patient.patientBasicInfo.patientId);
              }
              return hasName;
            });

          const currentList = [...this.roomList, ...newPatients];
          this.$store.dispatch('myPatients/rooms/setRoomList', currentList);
        }
      } catch (err) {
        console.error('loadRemainingPatients error:', err);
      } finally {
        this.isLoadingMore = false;
      }
    },

    async addUnreadCountToRoomList() {
      try {
        let { data } = await getLastSeenApi();
        let lastSeenList = data?.update || [];
        
        // Update last-seen data in Vuex store
        if (lastSeenList.length > 0) {
          this.$store.dispatch('myPatients/rooms/updateLastSeenMap', { 
            updates: lastSeenList 
          });
        }
        
        // Update unread count in room list
        lastSeenList.forEach(item => {
          let room = this.roomList.find(r => r.roomId === item.rid);
          if (room) {
            room.unreadCount = item.unread || 0;
          }
        });
      } catch (err) {
        console.error('addUnreadCountToRoomList error:', err);
      }
    },

    async createRoom(patientId, clinicId) {
      try {
        this.$emit('creatingRoom', true);
        const patient_id = String(patientId || '').trim();
        const clinic_id = String(clinicId || '').trim();
        const { data } = await createRoomApi({ patient_id, clinic_id });
        this.$emit('creatingRoom', false);
        return data.rid;
      } catch (e) {
        console.error('createRoom error:', e);
        this.$emit('creatingRoom', false);
        return null;
      }
    },

    async searchPatient() {
      try {
        this.searchPatientDropdown = [];
        if (this.inputPatientName.length > 1) {
          this.dropdownSearching = true;
          let { data } = await searchPatientApi({ inputPatient: this.inputPatientName });
          let patientList = data || [];
          patientList.forEach(patient => {
            let patientAddress = patient?.patient_address?.[0] || {};
            let backendPatient = { ...patient, patient_address: patientAddress };
            let patientInfo = this.mapBackendPatient(backendPatient);
            this.searchPatientDropdown.push(patientInfo);
          });
          this.dropdownSearching = false;
        }
      } catch (err) {
        console.error('searchPatient error:', err);
        this.searchPatientDropdown = [];
        this.dropdownSearching = false;
      }
    },

    onRemoteSearch(keyword) {
      this.inputPatientName = keyword || '';
      if (!this.inputPatientName || this.inputPatientName.length <= 1) {
        this.debounceSearchPatient.cancel();
        this.searchPatientDropdown = [];
        this.dropdownSearching = false;
        return;
      }
      this.debounceSearchPatient();
    },

    onPatientChange(value) {
      if (!value) return;
      const patient = this.searchPatientDropdown.find((p) => p.patientId === value);
      if (patient) {
        this.selectPatient(patient);
      }
      this.$nextTick(() => {
        this.selectedPatientValue = '';
        this.searchPatientDropdown = [];
        this.inputPatientName = '';
      });
    },

    /**
     * Map backend patient to patient info for frontend usage
     * @param backendPatient
     * backendPatient format:
     * {
     *   patient_id: string/number,
     *   patient_first_name: string,
     *   patient_middle_name: string,
     *   patient_last_name: string,
     *   patient_birthdate: string,
     *   patient_phone: string,
     *   patient_email: string,
     *   patient_gender: string,
     *   patient_address: {
     *     patient_street_address: string,
     *     patient_apt_po: string,
     *     patient_city: string,
     *     patient_state: string,
     *     patient_zip_code: string,
     *     patient_country: string,
     *   },
     * }
     * @returns patientInfo
     */
    mapBackendPatient(backendPatient) {
      let { patient_address } = backendPatient;
      let { patient_street_address, patient_apt_po, patient_city, patient_state, patient_zip_code, patient_country } = patient_address || {};
      let fullAddress = [patient_street_address, patient_apt_po, patient_city, patient_state, patient_zip_code, patient_country]
        .filter(Boolean).join(', ').trim();
      let patientInfo = {
        patientId: backendPatient?.patient_id?.toString() || '',
        firstName: backendPatient?.patient_first_name || '',
        middleName: backendPatient?.patient_middle_name || '',
        lastName: backendPatient?.patient_last_name || '',
        dob: backendPatient?.patient_birthdate || '',
        phone: backendPatient?.patient_phone || '',
        email: backendPatient?.patient_email || '',
        gender: backendPatient?.patient_gender || '',
        address: fullAddress || '',
        addressObj: {
          streetAddress: patient_street_address || '',
          aptPo: patient_apt_po || '',
          city: patient_city || '',
          state: patient_state || '',
          zipCode: patient_zip_code || '',
          country: patient_country || '',
        },
      }
      return patientInfo;
    },

    async selectPatient(patient) {
      this.isSearchFocused = false;
      try {
        let patientRoom = this.roomList.find(r => r.patientBasicInfo.patientId === patient.patientId);
        if (patientRoom) {
          // Case 1: patient is already in the list — open room and scroll into view
          await this.handleRoomClick(patientRoom);
          this.scrollToRoom(patient.patientId);
          return;
        }
        // Case 2: patient is not in the list — prepend, create room, activate
        await this.addPatientAndCreateRoom(patient);
      } catch (err) {
        console.error('selectPatient error:', err);
      }
    },

    async addPatientAndCreateRoom(patientInfo) {
      const tempRoom = {
        roomId: null,
        roomName: this.getPatientName(patientInfo),
        lastMessage: null,
        lastMessageTime: null,
        unreadCount: 0,
        patientBasicInfo: patientInfo,
        avatar: getAvatar(patientInfo.firstName, patientInfo.middleName, patientInfo.lastName),
      };
      this.$store.dispatch('myPatients/rooms/addRoom', { room: tempRoom });
      this.scrollToRoom(patientInfo.patientId);
      const newRoomId = await this.createRoom(patientInfo.patientId, this.clinicId);
      if (newRoomId) {
        this.$store.dispatch('myPatients/rooms/updateRoomId', {
          patientId: patientInfo.patientId,
          roomId: newRoomId,
        });
        this.$store.dispatch('myPatients/rooms/setCurrentRoomId', newRoomId);
        this.$emit('clickRoom', newRoomId);
      }
    },

    async scrollToRoom(patientId) {
      await this.$nextTick();
      const container = this.$refs.scrollContainer;
      const item = container?.querySelector(`[data-patient-id="${patientId}"]`);
      if (item) {
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    },

    clearSearch() {
      this.inputPatientName = '';
      this.debounceSearchPatient.cancel();
      this.searchPatientDropdown = [];
      this.dropdownSearching = false;
      this.isSearchFocused = false;
    },

    formatDateToMMDDYYYY(iso) {
      if (!iso) return '';
      if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(iso)) {
        // For compatibility with existing MM-DD-YYYY
        return iso.replace(/-/g, '/');
      }
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso).trim());
      if (!m) return '';
      return `${m[2]}/${m[3]}/${m[1]}`;
    },
    onScroll() {
      const el = this.$refs.scrollContainer;
      if (!el) return;
      const threshold = 60;
      const reachedBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
      if (reachedBottom && !this.isLoadingMore && this.hasMorePatients) {
        this.loadRemainingPatients();
      }
    },
    openAddPatientModal() { this.showAddPatientModal = true },
    closeAddPatientModal() { this.showAddPatientModal = false },
    async selectSupportRoom() {
      if (!this.supportRoom?.room_id) return;
      
      const roomId = this.supportRoom.room_id;
      
      // Mark as read if there are unread messages
      const lastSeenData = this.$store.getters['myPatients/rooms/getLastSeenByRoomId'](roomId);
      if (lastSeenData?.unread > 0) {
        try {
          await markAsReadApi({ rid: roomId });
        } catch (error) {
          console.error('Error marking support room as read:', error);
        }
      }
      
      this.$store.dispatch('myPatients/rooms/setUnreadCount', { roomId, unreadCount: 0 });
      this.$store.dispatch('myPatients/rooms/setCurrentRoomId', roomId);
      this.$emit('clickRoom', roomId);
    },
    async handleSavePatient(patientData) {
      try {
        this.closeAddPatientModal();
        const patientInfo = {
          ...patientData,
          address: "",
          addressObj: { streetAddress: "", aptPo: "", city: "", state: "", zipCode: "", country: "" },
        };
        await this.addPatientAndCreateRoom(patientInfo);
      } catch (e) {
        console.error('handleSavePatient error:', e);
      }
    },

    async handleRoomClick(room) {
      console.log("handleRoomClick room:", room);

      // Note: fetchPatientById is only called when EditContactAddressDialog opens
      // Patient data is already available in room.patientBasicInfo

      // If not RC room (no rid) → create first
      if (!room?.roomId) {
        const newRoomId = await this.createRoom(room.patientBasicInfo.patientId, this.clinicId);
        if (newRoomId) {
          this.$store.dispatch('myPatients/rooms/updateRoomId', {
            patientId: room.patientBasicInfo.patientId,
            roomId: newRoomId,
          });
          this.roomPatientIds.add(room.patientBasicInfo.patientId?.toString());
          this.$store.dispatch('myPatients/rooms/setCurrentRoomId', newRoomId);
          this.$emit('clickRoom', newRoomId);
        }
        return;
      }
      if (room.unreadCount > 0) {
        await markAsReadApi({ rid: room.roomId }); // call api to update last seen when clicked room has unread count
      }
      this.$store.dispatch('myPatients/rooms/setUnreadCount', { roomId: room.roomId, unreadCount: 0 })
      this.$store.dispatch('myPatients/rooms/setCurrentRoomId', room.roomId);
      this.$emit('clickRoom', room.roomId);
    },
    addNewRoomToList(patient, newRoomId) {
      if (!newRoomId) return;
      let newRoom = {
        roomId: newRoomId,
        roomName: this.getPatientName(patient),
        lastMessage: null,
        lastMessageTime: null,
        unreadCount: 0,
        patientBasicInfo: patient,
        avatar: getAvatar(patient.firstName, patient.middleName, patient.lastName),
      };
      this.$store.dispatch('myPatients/rooms/addRoom', { room: newRoom });
    },
    getPatientName(patient) {
      if (!patient) return 'Unknown';
      let { firstName, middleName, lastName } = patient;
      return [firstName, middleName, lastName]
        .filter(Boolean).join(' ').trim();
    },
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(w => w.charAt(0)).join('').toUpperCase().slice(0, 2);
    },
    getTimeAgo(lastMessageTime) {
      if (!lastMessageTime) return '';
      let localTime = moment.utc(lastMessageTime).local();
      let diff = moment().diff(localTime, "days");
      let timeDisplay = diff < 7 ?  localTime.fromNow(): localTime.format("MMM DD");
      return timeDisplay;
    },
  }
}

</script>

<style scoped>
.search-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 40;
}
.search-dropdown-item {
  transition: all 0.15s ease;
  border-left: 3px solid transparent;
}
.search-dropdown-item:hover {
  background: var(--Cool-White, #F9FBFD);
  border-left: 3px solid var(--VibrantDarkBlue, #004879);
}
.patient-list {
  display: flex;
  width: 334px;
  height: 100%;
  padding: 40px 24px 32px 24px;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  border-radius: 0 10px 10px 0;
  background: var(--Cool-White, #F9FBFD);
  overflow-y: auto;
}

.search-bar {
  display: flex;
  height: 40px;
  padding: 8px 24px;
  margin-bottom: 15px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  border-radius: 4px;
  border: 1px solid #C9C9C9;
  background: #FFF;
}

.search-bar-wrapper {
  margin-bottom: 15px;
}
.search-suffix-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
}

.search-input::placeholder {
  overflow: hidden;
  color: var(--ContentMediumGrey, #999);
  text-overflow: ellipsis;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.15px;
}

.clear-button {
  color: var(--color-Darkblue-000, #1E4670);
  text-align: center;
  font-family: "IBM Plex Sans Hebrew";
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  text-decoration-line: underline;
  text-underline-offset: auto;
}


.patient-item {
  height: 98px;
  border-radius: 5px;
  background: #FFF;
  box-shadow: 0 1px 15px 0 rgba(74, 74, 74, 0.27);
  transition: all 0.2s ease;
}

.patient-item:hover {
  border: 1px solid #004879;
  transition: none;
}

.patient-item:hover .patient-name {
  font-weight: 400;
}

.patient-item:hover .unread-badge-inline {
  background: #8BBDC7;
}

.patient-item.selected {
  border-left: 5px solid var(--VibrantDarkBlue, #004879);
  box-shadow: 0 1px 15px 0 rgba(74, 74, 74, 0.15);
}

.patient-name {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  line-clamp: 1;
  -webkit-line-clamp: 1;
  overflow: hidden;
  color: var(--TrueBlack, #333);
  text-overflow: ellipsis;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.15px;
}

.last-message-time {
  color: var(--Gray-40, #A8A8A8);
  font-family: Inter, sans-serif;
  font-size: 10px;
  font-weight: 400;
  line-height: 10px;
  letter-spacing: 0.2px;
  margin-bottom: 8px;
}

.unread-badge-inline {
  display: flex;
  width: 24px;
  height: 24px;
  padding: 2px;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  background: #004879;
  color: #FFF;
  text-align: center;
  font-family: "IBM Plex Sans Hebrew";
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
}

.last-message-preview {
  color: var(--SecondaryBlack, #666);
  font-family: "IBM Plex Sans Hebrew";
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  word-break: break-word;
}

.name-initial {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background-color: #F2F2F2;
  color: #979797;
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
}

.loading-more-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
}

.loading-more-container .rotating-circle {
  width: 48px;
  height: 48px;
  transform-origin: center;
  animation: rotate 1.6s linear infinite;
}

.search-dropdown-arrow {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  transform-origin: center;
  animation: rotate 1.6s linear infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

.loading-more-text {
  font-size: 14px;
  font-family: "IBM Plex Sans Hebrew";
  line-height: 20px;
  letter-spacing: 0.25px;
  color: var(--color-Darkblue-000, #1E4670);
}

/* Support Room Styling */
.support-room-item {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
}

.support-room-item:hover {
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  border-color: #7dd3fc;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 72, 121, 0.1);
}

.support-room-item.active-room {
  background: linear-gradient(135deg, #bae6fd 0%, #7dd3fc 100%);
  border-color: #0ea5e9;
  box-shadow: 0 2px 12px rgba(0, 72, 121, 0.15);
}

.support-room-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
}

.support-room-name {
  color: #0c4a6e;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.15px;
  margin-bottom: 2px;
}

.support-room-subtitle {
  color: #0369a1;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.2px;
  margin-top: 4px;
}
</style>
